import {
  AgentRegistrySetupFailure,
  AgentRegistrySetupReason
} from '@tryiris-ai/common.protojs/api/v1/agent_registry';

/**
 * Decoded Bridge Setup failure (ENG-4483 W2). The server carries it in
 * `api.v1.Error.context` as a marshaled AgentRegistrySetupFailure so the client
 * branches on the reason, never on error strings. `correlationId` is the
 * opaque support reference (request or operation ID) and is safe to display.
 */
export interface SetupFailure {
  readonly reason: AgentRegistrySetupReason;
  readonly correlationId: string;
}

const MAX_CAUSE_DEPTH = 8;

// Realm-safe: the envelope's bytes can come from a different global (jsdom,
// worker, transport polyfill), where `instanceof Uint8Array` is false.
const isByteContext = (value: unknown): value is Uint8Array =>
  Object.prototype.toString.call(value) === '[object Uint8Array]' &&
  (value as Uint8Array).byteLength > 0;

/**
 * Walk an error's `cause` chain for the protobuf Error envelope that
 * `wrapApiCall` attaches and decode its setup failure. Returns undefined for
 * legacy/generic errors (no context), malformed context, or an unspecified
 * reason, so callers fall back to their existing code-based classification.
 * Bounded depth: a cyclic cause chain must not hang the picker.
 */
export const readSetupFailure = (error: unknown): SetupFailure | undefined => {
  let current: unknown = error;
  for (
    let depth = 0;
    depth < MAX_CAUSE_DEPTH && current !== null && current !== undefined;
    depth++
  ) {
    if (typeof current === 'object' && 'context' in current) {
      const context = (current as { context: unknown }).context;
      if (isByteContext(context)) {
        try {
          const failure = AgentRegistrySetupFailure.decode(context);
          // ts-proto decodes unknown enum numbers as-is; only a known,
          // specified reason is actionable for the picker.
          if (
            failure.reason !== AgentRegistrySetupReason.AGENT_REGISTRY_SETUP_REASON_UNSPECIFIED &&
            Object.values(AgentRegistrySetupReason).includes(failure.reason)
          ) {
            return { reason: failure.reason, correlationId: failure.correlationId };
          }
        } catch {
          // Not a setup failure envelope; keep the code-based classification.
        }
        return undefined;
      }
    }
    current = typeof current === 'object' ? (current as { cause?: unknown }).cause : undefined;
  }
  return undefined;
};
